// src/services/sync/engine.js
// Background sync orchestrator.
// Priority queue drain → dispatch to Firestore → conflict reconcile
// → cache invalidation → Sentry breadcrumbs.

import { captureOfflineFailure, captureSOSFailure, breadcrumbSync } from '@services/monitoring';
import { parseSyncPayload } from '@services/validation';
import { useOfflineStore } from '@store/offlineStore';
import { SyncWorker } from '@workers';
import { SyncQueue, APICache } from '../storage/idb.js';
import { FEATURES } from '@config';

const SYNC_INTERVAL_ONLINE_MS  = 15_000;
const SYNC_INTERVAL_OFFLINE_MS = 60_000;
const MAX_CONCURRENT           = 3;
const BATCH_SIZE               = 50;   // max items per sync packet after outage
const BATCH_DELAY_MS           = 200;  // pause between batches (backpressure)
const MAX_ATTEMPTS             = 4;    // total attempts before dead letter
const PRIORITY_TIERS = {
  sos:     5,   // Life safety — sync first, no batching delay
  flood:   4,   // Active hazard
  high:    3,   // Road/power outage
  normal:  2,   // Standard report
  low:     1,   // Background data
};
const BACKOFF_SCHEDULE         = [0, 5_000, 30_000, 120_000, 480_000];  // 0s, 5s, 30s, 2m, 8m


// ── Duplicate report prevention ────────────────────────────────────
// Blocks the same report type in the same zone within 10 minutes.
// SOS is never blocked — a user may need to broadcast multiple times.
const DEDUP_WINDOW_MS = 10 * 60 * 1000;

export async function checkForDuplicate(type, zone) {
  if (!type || type === 'sos' || type === 'create_sos') return null;
  try {
    const all    = await SyncQueue.getAll();
    const cutoff = Date.now() - DEDUP_WINDOW_MS;
    const recent = all.filter(item =>
      item.payload?.type === type &&
      item.payload?.zone === zone &&
      item.createdAt     > cutoff &&
      item.status !== 'dead'
    );
    if (recent.length > 0) {
      const minsAgo = Math.round((Date.now() - recent[0].createdAt) / 60_000);
      return `You already posted a ${type} report for ${zone} ${minsAgo} min ago.`;
    }
  } catch {}
  return null;
}

let _running   = false;
let _timer     = null;
let _online    = typeof navigator !== 'undefined' ? navigator.onLine : true;
let _listeners = new Set();

// ── Public API ────────────────────────────────────────────────────
export async function startSyncEngine() {
  if (_running) return;
  _running = true;

  // Reset any items stuck in 'syncing' from a prior crashed session
  const stuck = await SyncQueue.resetSyncing();
  if (stuck > 0) console.info(`[SyncEngine] Reset ${stuck} stuck items`);

  window.addEventListener('online',          _onOnline);
  window.addEventListener('offline',         _onOffline);
  window.addEventListener('na-sync-complete', _onOnline);
  window.addEventListener('visibilitychange', _onVisibility);

  _scheduleNext();
  console.info('[SyncEngine] Started');
}

export function stopSyncEngine() {
  _running = false;
  clearTimeout(_timer);
  window.removeEventListener('online',          _onOnline);
  window.removeEventListener('offline',         _onOffline);
  window.removeEventListener('na-sync-complete', _onOnline);
  window.removeEventListener('visibilitychange', _onVisibility);
}

export async function drainQueue() {
  // Manually trigger a sync cycle — used by retry logic
  if (_running && _online) {
    console.log('[SyncEngine] Manual drain triggered');
    await _runCycle();
  }
}

export function onSyncEvent(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

// All writes flow through here — never call SyncQueue.add() from product code.
export async function enqueue(opType, payload, opts = {}) {
  const item = await SyncQueue.add({
    type:        opType,
    payload,
    priority:    opts.priority    ?? undefined,
    maxAttempts: opts.maxAttempts ?? undefined,
    syncId:      opts.syncId      ?? undefined,
  });
  _emit('queued', item);
  if (_online) _runCycle();
  return item.syncId;
}

// ── Cycle ─────────────────────────────────────────────────────────
async function _runCycle() {
  if (!_online) return;

  const pending = await SyncQueue.getPending();
  if (pending.length === 0) return;

  // ── Batch processing ─────────────────────────────────────────────
  // After an outage, hundreds of queued items might arrive simultaneously.
  // Process in chunks of BATCH_SIZE with a short delay between batches
  // to avoid overwhelming Firestore with write bursts.
  const topPriority = pending[0].priority;
  const tierItems   = pending.filter((i) => i.priority === topPriority);

  // Slice into chunks
  const chunks = [];
  for (let i = 0; i < tierItems.length; i += BATCH_SIZE) {
    chunks.push(tierItems.slice(i, i + BATCH_SIZE));
  }

  for (let chunkIdx = 0; chunkIdx < chunks.length; chunkIdx++) {
    const chunk = chunks[chunkIdx];

    // Process up to MAX_CONCURRENT within the chunk
    for (let i = 0; i < chunk.length; i += MAX_CONCURRENT) {
      await Promise.allSettled(chunk.slice(i, i + MAX_CONCURRENT).map(_processItem));
    }

    // Backpressure: pause between batches if more chunks remain
    if (chunkIdx < chunks.length - 1) {
      await new Promise(r => setTimeout(r, BATCH_DELAY_MS));
    }
  }

  const counts = await SyncQueue.countByStatus();
  _emit('cycle_complete', null, { counts });
  // Push counts into the UI store so components can react without polling IDB
  useOfflineStore.getState().setQueueCounts(counts);

  // Chain immediately if more pending
  const remaining = await SyncQueue.getPending();
  if (remaining.length > 0) setTimeout(_runCycle, 250);
}

// ── Process one item ──────────────────────────────────────────────
async function _processItem(item) {
  await SyncQueue.markSyncing(item.syncId);
  _emit('syncing', item);

  try {
    const serverResult = await _dispatch(item);
    await _reconcile(item, serverResult);
    await SyncQueue.markDone(item.syncId);
    _emit('done', item);
    // Optimistic store is already updated — invalidate the read-cache
    // so the next fetch brings server-confirmed data
    await _invalidateCache(item.type);
  } catch (err) {
    const result = await SyncQueue.markFailed(item.syncId, err.message);
    _emit('failed', item, { error: err.message, ...result });
    captureOfflineFailure(item.syncId, item.type, result.attempts, err.message);

    if (result.dead) {
      _emit('dead', item, { error: err.message, attempts: result.attempts });
      useOfflineStore.getState().addDeadItem({ ...item, lastError: err.message });
      if (item.type === 'create_sos') {
        captureSOSFailure(item.syncId, item.payload?.body);
        useOfflineStore.getState().setSosFailure({ syncId: item.syncId, body: item.payload?.body });
        _sosEmergencyFallback(item);
      }
    }
  }
}

// ── Dispatch: write to Firestore (or REST if Firebase unavailable) ─
async function _dispatch(item) {
  const { type, payload } = item;

  // Firestore path
  if (FEATURES.firebase) {
    return _firestoreDispatch(type, payload);
  }

  // REST fallback (Cloud Functions)
  return _restDispatch(type, payload);
}

async function _firestoreDispatch(type, payload) {
  // Validate payload before any Firestore write
  const validation = parseSyncPayload(type, payload);
  if (!validation.ok) {
    const knownTypes = ['create_report','create_sos','update_gas','update_shelter',
                        'confirm_incident','deny_incident','report_connectivity','confirm_connectivity'];
    if (knownTypes.includes(type)) {
      console.error('[SyncEngine] Validation failed:', type, validation.errors);
      throw new Error(`Validation failed [${type}]: ${validation.errors.join('; ')}`);
    }
  }
  // Use cleaned, stripped payload if validation passed
  const safePayload = validation.ok ? (validation.data ?? payload) : payload;
  return _firestoreDispatchInner(type, safePayload);
}

async function _firestoreDispatchInner(type, payload) {
  const { initFirebase }      = await import('../storage/firebase.js');
  const { doc, setDoc, updateDoc, increment, serverTimestamp } =
    await import('firebase/firestore');

  const { db, auth } = await initFirebase();
  if (!db) throw new Error('Firestore not initialized');
  // Log auth state for debugging
  switch (type) {
    case 'create_sos':
    case 'create_report': {
      const ref = doc(db, 'reports', payload.id);
      // Strip private/internal fields (prefixed _) before writing to Firestore
      // These are local-only: _localPhotoDataUrl, _photoMeta, _pendingUpload, _viewContext etc.
      const firestorePayload = Object.fromEntries(
        Object.entries(payload).filter(([k]) => !k.startsWith('_'))
      );
      try {
        await setDoc(ref, { ...firestorePayload, serverCreatedAt: serverTimestamp() });
        console.log('[SyncEngine] Report written successfully:', ref.id);
        return { serverVersion: Date.now() };
      } catch (writeErr) {
        console.error('[SyncEngine] Firestore write failed:', writeErr.code, writeErr.message);
        console.error('[SyncEngine] Payload fields:', Object.keys(firestorePayload).join(', '));
        throw writeErr;
      }
    }

    case 'update_gas': {
      // Gas updates are upserts — create or merge
      const ref = doc(db, 'gas_stations', payload.stationId);
      await setDoc(ref, {
        ...payload,
        serverUpdatedAt: serverTimestamp(),
      }, { merge: true });
      return { serverVersion: Date.now() };
    }

    case 'update_shelter': {
      const ref = doc(db, 'shelters', payload.shelterId);
      await setDoc(ref, {
        ...payload,
        serverUpdatedAt: serverTimestamp(),
      }, { merge: true });
      return { serverVersion: Date.now() };
    }

    case 'confirm_report': {
      const { doc, setDoc, updateDoc, increment, serverTimestamp } = await import('firebase/firestore');
      const ref = doc(db, 'reports', payload.reportId);
      // Distributed counter — shard writes across 10 sub-docs to avoid
      // hotspot contention when 50K users confirm the same report simultaneously.
      // A rollup function periodically sums shards into confirmCount on the parent.
      const shardId = Math.floor(Math.random() * 10).toString();
      const shardRef = doc(db, 'reports', payload.reportId, '_counters', 'confirmCount_' + shardId);
      await setDoc(shardRef, { count: increment(1), updatedAt: serverTimestamp() }, { merge: true });
      return null;
    }

    case 'flag_report': {
      const ref = doc(db, 'reports', payload.reportId);
      const shardId = Math.floor(Math.random() * 10).toString();
      const shardRef = doc(db, 'reports', payload.reportId, '_counters', 'flagCount_' + shardId);
      await setDoc(shardRef, { count: increment(1), updatedAt: serverTimestamp() }, { merge: true });
      return null;
    }

    case 'confirm_incident':
    case 'deny_incident': {
      const { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp } =
        await import('firebase/firestore');
      const field = type === 'confirm_incident' ? 'confirmCount' : 'denyCount';
      const vote  = type === 'confirm_incident' ? 'confirm' : 'deny';

      // ── Self-confirmation guard ────────────────────────────────
      // A user cannot confirm an incident that originated from their own report.
      // Also enforces one-vote-per-user via a per-user subcollection record.
      if (payload.authorId) {
        const incRef  = doc(db, 'incidents', payload.incidentId);
        const incSnap = await getDoc(incRef);

        if (incSnap.exists()) {
          const inc = incSnap.data();

          // Block if this incident was escalated from the author's own report
          if (inc.originReportId) {
            const originSnap = await getDoc(doc(db, 'reports', inc.originReportId)).catch(() => null);
            if (originSnap?.exists() && originSnap.data().authorId === payload.authorId) {
              console.warn('[engine] Self-confirmation blocked:', payload.authorId);
              return { blocked: true, reason: 'self_confirmation' };
            }
          }
        }

        // Write per-user vote to prevent double voting
        const voteRef      = doc(db, 'incidents', payload.incidentId, 'confirmations', payload.authorId);
        const existingVote = await getDoc(voteRef).catch(() => null);
        if (existingVote?.exists()) {
          return { blocked: true, reason: 'already_voted' };
        }
        await setDoc(voteRef, {
          userId:    payload.authorId,
          vote,
          createdAt: new Date().toISOString(),
        });
      }

      // Increment the parent incident's denormalized count
      await updateDoc(doc(db, 'incidents', payload.incidentId), {
        [field]:         increment(1),
        serverUpdatedAt: serverTimestamp(),
      });
      return null;
    }

    case 'post_incident_update': {
      const { doc, collection, setDoc, serverTimestamp } = await import('firebase/firestore');
      const updateRef = doc(db, 'incidents', payload.incidentId, 'updates', payload.update.updateId);
      await setDoc(updateRef, { ...payload.update, serverCreatedAt: serverTimestamp() });
      // Apply status change to parent doc if present
      if (payload.update.statusChange) {
        const { updateDoc } = await import('firebase/firestore');
        await updateDoc(doc(db, 'incidents', payload.incidentId), {
          status:      payload.update.statusChange,
          updatedAt:   payload.update.createdAt,
          serverUpdatedAt: serverTimestamp(),
        });
      }
      return null;
    }

    case 'update_incident_status': {
      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      await updateDoc(doc(db, 'incidents', payload.incidentId), {
        status:      payload.status,
        resolvedAt:  payload.status === 'resolved' ? payload.resolvedAt ?? new Date().toISOString() : null,
        serverUpdatedAt: serverTimestamp(),
      });
      return null;
    }

    case 'report_connectivity': {
      // Community-submitted new hotspot
      const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
      const ref = doc(db, 'connectivity_points', payload.id);
      await setDoc(ref, { ...payload, serverCreatedAt: serverTimestamp() }, { merge: false });
      return { serverId: payload.id };
    }

    case 'confirm_connectivity': {
      const { doc, updateDoc, increment, serverTimestamp } = await import('firebase/firestore');
      const field = payload.working ? 'confirmCount' : 'denyCount';
      await updateDoc(doc(db, 'connectivity_points', payload.pointId), {
        [field]:         increment(1),
        status:          payload.working ? 'active' : 'offline',
        lastConfirmedAt: new Date().toISOString(),
        serverUpdatedAt: serverTimestamp(),
      });
      return null;
    }

    default:
      throw new Error(`Unknown op type: ${type}`);
  }
}

const FUNCTIONS_BASE = import.meta.env.VITE_FUNCTIONS_URL
  ?? 'https://us-central1-eyeonstorm-e7bff.cloudfunctions.net';

async function _restDispatch(type, payload) {
  const routes = {
    create_sos:     ['POST',  '/api/reports'],
    create_report:  ['POST',  '/api/reports'],
    update_gas:     ['POST',  '/api/gas-stations'],
    update_shelter: ['POST',  '/api/shelters'],
    confirm_report: ['PATCH', `/api/reports/${payload.reportId}/confirm`],
    flag_report:    ['PATCH', `/api/reports/${payload.reportId}/flag`],
  };

  const [method, path] = routes[type] ?? [];
  if (!method) throw new Error(`No REST route for: ${type}`);

  // REST fallback endpoints not yet implemented.
  // Fail gracefully so the offline queue retries when Firebase recovers.
  console.warn('[SyncEngine] REST fallback not available for:', type, path);
  throw new Error('REST_NOT_IMPLEMENTED: ' + type);
}

// ── Conflict reconciliation ───────────────────────────────────────
// Strategy matrix:
//   priority ≥ 4 (SOS, flood, fire)  → CLIENT WINS always (life safety)
//   update_gas / update_shelter        → SERVER WINS if >5min newer (consensus)
//   confirm / flag                     → MERGE (idempotent server increment)
//   everything else                    → LAST WRITER WINS (version ms)

async function _reconcile(item, serverResult) {
  if (!serverResult?.serverVersion) return;   // no version info — accept as-is
  if (serverResult.serverVersion <= item.version) return;  // no conflict

  const { type, payload, priority } = item;

  if (priority >= 4) {
    // Life-safety: force-write regardless of server state
    await _dispatch({ ...item, payload: { ...payload, _forceWrite: true } });
    _emit('conflict_resolved', item, { strategy: 'client_wins' });
    return;
  }

  if (type === 'update_gas' || type === 'update_shelter') {
    const serverAge = serverResult.serverVersion - item.version;
    if (serverAge > 5 * 60_000) {
      // Server is >5min newer — server wins; broadcast so UI refreshes
      window.dispatchEvent(new CustomEvent('na-sync-reconciled', {
        detail: { opType: type, serverData: serverResult.serverData },
      }));
      _emit('conflict_resolved', item, { strategy: 'server_wins' });
    } else {
      _emit('conflict_resolved', item, { strategy: 'client_wins_recent' });
    }
    return;
  }

  if (type === 'confirm_report' || type === 'flag_report') {
    _emit('conflict_resolved', item, { strategy: 'merge' });
    return;
  }

  _emit('conflict_resolved', item, { strategy: 'lww' });
}

// ── Cache invalidation after write ────────────────────────────────
async function _invalidateCache(opType) {
  const { StaleCacheManager } = await import('./staleCache.js');
  await StaleCacheManager.invalidateForWrite(opType).catch(() => {});
}

// ── SOS emergency fallback (retries exhausted) ────────────────────
function _sosEmergencyFallback(item) {
  const body = item.payload?.body ?? 'Emergency help needed';

  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('SOS broadcast failed — CALL 911 NOW', {
      body:               `Could not send: ${body.substring(0, 120)}`,
      icon:               '/icon-192.png',
      requireInteraction: true,
      tag:                'na-sos-failed',
      silent:             false,
    });
  }

  navigator.vibrate?.([500, 200, 500, 200, 500, 200, 1000]);

  window.dispatchEvent(new CustomEvent('na-sos-failed', {
    detail: { syncId: item.syncId, body },
  }));
}

// ── Scheduler ────────────────────────────────────────────────────
function _scheduleNext() {
  if (!_running) return;
  const ms = _online ? SYNC_INTERVAL_ONLINE_MS : SYNC_INTERVAL_OFFLINE_MS;
  _timer = setTimeout(async () => {
    await _runCycle();
    // Prune in worker — cursor scan shouldn't block render
    SyncWorker.pruneDoneItems().catch(() => SyncQueue.pruneDone().catch(() => {}));
    _scheduleNext();
  }, ms);
}

function _onOnline()     { _online = true;  _runCycle(); }
function _onOffline()    { _online = false; }
function _onVisibility() { if (!document.hidden && _online) _runCycle(); }

// ── Event emitter ─────────────────────────────────────────────────
function _emit(type, item, extra = {}) {
  const event = {
    type,
    syncId:   item?.syncId   ?? null,
    opType:   item?.type     ?? null,
    priority: item?.priority ?? null,
    ...extra,
  };
  _listeners.forEach((cb) => { try { cb(event); } catch {} });
  useOfflineStore.getState().setSyncEvent(event);
  if (['syncing', 'done', 'failed', 'dead'].includes(type)) {
    breadcrumbSync(item?.type, type, item?.syncId);
  }
}
